// Home.tsx – 간단한 원페이지
import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useNavigate } from 'react-router-dom'
import DefaultLayout from '../components/layouts/DefaultLayout'
import {
  Box,
  Button,
  Text,
  VStack,
  HStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Input,
  useToast,
  useDisclosure,
  Spinner,
  FormControl,
  FormLabel,
  Stack,
  Badge,
  Icon,
  Divider,
  Switch,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  IconButton,
  Wrap,
  WrapItem,
  Radio,
  RadioGroup,
  Heading,
  Container,
  Flex
} from '@chakra-ui/react'
import {
  LockIcon,
  CheckCircleIcon,
  CloseIcon,
  SettingsIcon,
  AddIcon
} from '@chakra-ui/icons'
import { FaTelegram } from 'react-icons/fa'
import { FiSettings } from 'react-icons/fi'
import { useRecoilState } from 'recoil'
import { authState } from '../atoms/auth'
import {
  signIn,
  signOut,
  updateTrading,
  updateKeyAndSecret,
  getSymbolUsdtPrice
} from '../services/apiService'
import { useAuthState } from '../utils/authUtils'
import ApiManual from '../components/ApiManual'
import RobotVisual from '../components/RobotVisual'
import AiWorkingOverlay from '../components/Trading/AiWorkingOverlay'
import RealtimeList from '../components/RealtimeList'

type RoiTrigger = {
  roi: string
  size: string
}

const Home: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [auth, setAuth] = useRecoilState(authState)
  const { reloadUser, initUser } = useAuthState()
  const toast = useToast()

  // 로그인 모달 상태
  const {
    isOpen: isLoginOpen,
    onOpen: onLoginOpen,
    onClose: onLoginClose
  } = useDisclosure()

  // API 관리 모달 상태
  const {
    isOpen: isApiOpen,
    onOpen: onApiOpen,
    onClose: onApiClose
  } = useDisclosure()

  // 시드 입력 모달 상태
  const {
    isOpen: isSeedOpen,
    onOpen: onSeedOpen,
    onClose: onSeedClose
  } = useDisclosure()

  // 로그인 입력값
  const [account, setAccount] = useState('')
  const [password, setPassword] = useState('')
  const [isLoggingIn, setIsLoggingIn] = useState(false)

  // API 관리 입력값
  const [apiKey, setApiKey] = useState('')
  const [apiSecret, setApiSecret] = useState('')

  // 시드 입력값
  const [seedAmount, setSeedAmount] = useState('')
  const [strategyRatio, setStrategyRatio] = useState('7:3') // '9:1', '8:2', '7:3'

  // 수동 설정 입력값 (관리자용)
  const [useManualConfig, setUseManualConfig] = useState(false) // 수동 설정 사용 여부
  const [manualLeverage, setManualLeverage] = useState('30')
  const [manualTakeProfit, setManualTakeProfit] = useState('10')
  const [manualStopLoss, setManualStopLoss] = useState('0')
  const [manualSize, setManualSize] = useState('1')
  const [manualDcaCount, setManualDcaCount] = useState('50')
  const [manualRoi, setManualRoi] = useState('-30')
  const [manualRoiTriggers, setManualRoiTriggers] = useState<RoiTrigger[]>([])

  // 사이즈 계산기 상태
  const [showCalculator, setShowCalculator] = useState(false)
  const [calcSeed, setCalcSeed] = useState('1000')
  const [calcSeedPercent, setCalcSeedPercent] = useState(100) // 시드 사용 비율 (%)
  const [calcDivide, setCalcDivide] = useState('50')
  const [xrpPrice, setXrpPrice] = useState('0.0000')

  // 자동매매 시작/종료 상태
  const [isTradingLoading, setIsTradingLoading] = useState(false)

  // 관리자 체크
  const isAdmin = useMemo(() => auth?.isAdmin === 1, [auth?.isAdmin])

  // 현재 자동매매 상태 확인
  const currentTrading = useMemo(() => {
    if (!auth?.id || !Array.isArray(auth?.trading)) return null
    // XRP 우선, 없으면 첫 번째
    const xrpTrading = auth.trading.find((t) => t.symbol === 'XRP')
    return xrpTrading || auth.trading[0] || null
  }, [auth])

  const isAutoTradingRunning = useMemo(() => {
    return currentTrading?.botStatus === 1
  }, [currentTrading])

  const selectedSymbol = useMemo(() => {
    return currentTrading?.symbol || 'XRP'
  }, [currentTrading])

  // API 키 초기화
  useEffect(() => {
    if (auth?.apiKey) setApiKey(auth.apiKey)
    if (auth?.apiSecret) setApiSecret(auth.apiSecret)
  }, [auth])

  // 페이지 로드 시 사용자 정보 확인
  useEffect(() => {
    if (auth?.id) {
      reloadUser().catch(() => {
        // 에러 무시
      })
    }
  }, [])

  // 로그인 처리
  const handleLogin = async () => {
    if (!account.trim() || !password.trim()) {
      toast({
        title: t('입력 오류'),
        description: t('아이디와 비밀번호를 입력해주세요.'),
        status: 'warning',
        duration: 2000,
        isClosable: true
      })
      return
    }

    setIsLoggingIn(true)
    try {
      const { data: res } = await signIn({
        account: account.trim(),
        password: password.trim()
      })
      if (res.statusCode === 200) {
        setAuth({
          ...res.data
        })
        toast({
          title: t('로그인 성공'),
          description: t('환영합니다!'),
          status: 'success',
          duration: 2000,
          isClosable: true
        })
        onLoginClose()
        setAccount('')
        setPassword('')
        await reloadUser()
      }
    } catch (err: any) {
      toast({
        title: t('로그인 실패'),
        description:
          err.response?.data?.message ||
          t('아이디 또는 비밀번호가 일치하지 않습니다.'),
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    } finally {
      setIsLoggingIn(false)
    }
  }

  // API 저장 처리
  const handleSaveApi = async () => {
    try {
      const { data: res } = await updateKeyAndSecret({
        key: apiKey,
        secret: apiSecret
      })
      if (res.statusCode === 200) {
        toast({
          title: t('API 관리'),
          description: res.message,
          status: 'success',
          duration: 2000,
          isClosable: true
        })
        onApiClose()
        await reloadUser()
      }
    } catch (err: any) {
      toast({
        title: t('API 관리'),
        description:
          err.response?.data?.message || t('알 수 없는 오류가 발생했습니다.'),
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    }
  }

  // 자동매매 시작 - 관리자는 수동 설정, 일반 유저는 시드 입력
  const handleStartAutoTrading = () => {
    if (!auth?.id) {
      toast({
        title: t('로그인 필요'),
        description: t('자동매매를 시작하려면 먼저 로그인해주세요.'),
        status: 'warning',
        duration: 2000,
        isClosable: true
      })
      onLoginOpen()
      return
    }

    if (!currentTrading) {
      toast({
        title: t('설정 필요'),
        description: t('자동매매 설정이 없습니다. 먼저 설정을 완료해주세요.'),
        status: 'warning',
        duration: 2000,
        isClosable: true
      })
      return
    }

    // 시드 입력 모달 열기 (관리자는 스위치 옵션 포함)
    setSeedAmount('')
    setUseManualConfig(false) // 기본값은 시드 입력 방식

    // 관리자인 경우 수동 설정 기본값 불러오기
    if (isAdmin) {
      setManualLeverage(String(currentTrading.leverage || 30))
      setManualTakeProfit(String(currentTrading.tp || 10))
      setManualStopLoss(String(currentTrading.sl || 0))
      setManualSize(String(currentTrading.size || 1))
      setManualDcaCount(String(currentTrading.addOrder || 50))
      setManualRoi(String(currentTrading.dynamic?.[0]?.roi || -30))
      
      // 물타기 설정 불러오기
      if (Array.isArray(currentTrading.dynamic) && currentTrading.dynamic.length > 0) {
        const roiList = currentTrading.dynamic.map((item) => ({
          roi: String(item.roi),
          size: String(item.size)
        }))
        setManualRoiTriggers(roiList)
      } else {
        // 기본값으로 50개의 -30% ROI 트리거 생성
        const defaultTriggers = Array.from({ length: 50 }, () => ({
          roi: '-30',
          size: String(currentTrading.size || 1)
        }))
        setManualRoiTriggers(defaultTriggers)
      }
    }

    onSeedOpen()
  }

  // XRP 가격 조회 (수동 설정 모드일 때)
  useEffect(() => {
    const fetchXrpPrice = async () => {
      if (!isSeedOpen || !useManualConfig || !isAdmin) return

      try {
        const { data: priceRes } = await getSymbolUsdtPrice('XRP')
        if (priceRes.statusCode === 200 && priceRes.data) {
          const price = Number(priceRes.data)
          setXrpPrice(price.toFixed(4))
        }
      } catch (error) {
        console.error('XRP 가격 조회 실패:', error)
      }
    }

    fetchXrpPrice()
  }, [isSeedOpen, useManualConfig, isAdmin])

  // 사이즈 계산 로직
  const calculatedSize = useMemo(() => {
    const seed = Number(calcSeed) || 0
    const seedPercent = Number(calcSeedPercent) || 0
    const leverage = Number(manualLeverage) || 0
    const divide = Number(calcDivide) || 1
    const price = Number(xrpPrice) || 0

    if (seed === 0 || leverage === 0 || divide === 0 || price === 0) {
      return 0
    }

    // 비율 적용된 시드
    const usedSeed = seed * (seedPercent / 100)
    // 회차당 실제 진입 금액
    const entryAmount = usedSeed / divide
    // 레버리지 적용 금액
    const leveragedAmount = entryAmount * leverage
    // 코인 수량
    const coinAmount = leveragedAmount / price
    // Size 계산 (XRP는 1 size = 10 XRP)
    const sizeUnits = Math.floor(coinAmount / 10)

    return sizeUnits
  }, [calcSeed, calcSeedPercent, manualLeverage, calcDivide, xrpPrice])

  // 계산된 사이즈 적용
  const handleApplyCalculatedSize = () => {
    setManualSize(String(calculatedSize))
    setShowCalculator(false)
    toast({
      title: t('사이즈 적용'),
      description: t('계산된 사이즈 {{size}}이(가) 적용되었습니다.', { size: calculatedSize }),
      status: 'success',
      duration: 2000,
      isClosable: true
    })
  }

  // 수동 물타기 단계 추가
  const addManualRoiTrigger = () => {
    const last = manualRoiTriggers[manualRoiTriggers.length - 1] || { roi: '-30', size: '1' }
    setManualRoiTriggers([...manualRoiTriggers, { roi: last.roi, size: last.size }])
  }

  // 수동 물타기 단계 삭제
  const removeManualRoiTrigger = (index: number) => {
    const updated = [...manualRoiTriggers]
    updated.splice(index, 1)
    setManualRoiTriggers(updated)
  }

  // 수동 물타기 일괄 설정
  const handleBatchSetupManual = () => {
    const count = Math.max(1, Number(manualDcaCount))
    const roi = Number(manualRoi)
    const size = Number(manualSize)
    const triggers = Array.from({ length: count }, () => ({ roi: String(roi), size: String(size) }))
    setManualRoiTriggers(triggers)
    toast({
      title: t('일괄 설정 완료'),
      description: t('{{count}}개의 물타기 단계가 생성되었습니다.', { count }),
      status: 'success',
      duration: 2000,
      isClosable: true
    })
  }


  // 시드 입력 후 실제 자동매매 시작
  const handleConfirmStartWithSeed = async () => {
    if (!seedAmount || isNaN(Number(seedAmount)) || Number(seedAmount) <= 0) {
      toast({
        title: t('입력 오류'),
        description: t('유효한 시드 금액을 입력해주세요.'),
        status: 'warning',
        duration: 2000,
        isClosable: true
      })
      return
    }

    setIsTradingLoading(true)
    try {
      const totalSeed = Number(seedAmount)
      
      // 전략 비율에 따른 진입 비중 설정
      let entryRatio = 0.3 // 기본 7:3 (30%)
      if (strategyRatio === '9:1') entryRatio = 0.1 // 10%
      else if (strategyRatio === '8:2') entryRatio = 0.2 // 20%

      const availableSeed = totalSeed * entryRatio
      const dcaCount = 100 // 물타기 회수
      const roi = -30 // ROI -30%
      const leverage = currentTrading?.leverage || 30 // 기본 레버리지 30
      const tp = 10 // TP 10%
      const sl = 0 // 손절 비활성화
      const symbol = 'XRP'

      // XRP 가격 조회
      const { data: priceRes } = await getSymbolUsdtPrice(symbol)
      if (priceRes.statusCode !== 200 || !priceRes.data) {
        throw new Error('XRP 가격 조회에 실패했습니다.')
      }
      const xrpPrice = Number(priceRes.data)

      if (!xrpPrice || xrpPrice <= 0) {
        throw new Error('유효하지 않은 XRP 가격입니다.')
      }

      // 사이즈 계산 (Strategy.tsx의 로직 참고)
      // 회차당 마진
      const margin = availableSeed / dcaCount // $300 / 100 = $3
      // 가치 = 마진 * 레버리지
      const value = margin * leverage // $3 * 30 = $90
      // 코인 수량 = 가치 / 가격
      const coinAmount = value / xrpPrice
      // Size 계산: 코인 수량 / (1 Size 단위), 내림
      // XRP는 1 Size = 10 XRP
      const unit = 10 // XRP는 10 XRP가 1 size
      const sizeUnits = Math.floor(coinAmount / unit)

      if (sizeUnits < 1) {
        toast({
          title: t('시드 부족'),
          description: t(
            '최소 시드 금액이 부족합니다. 더 큰 금액을 입력해주세요.'
          ),
          status: 'warning',
          duration: 3000,
          isClosable: true
        })
        setIsTradingLoading(false)
        return
      }

      // 물타기 dynamic 배열 생성 (100개, 각각 roi: -30, size: 계산된 값)
      const roiTriggers = Array.from({ length: dcaCount }, () => ({
        roi,
        size: sizeUnits
      }))

      // XRP 트레이딩 업데이트 및 봇 시작
      const { data: res } = await updateTrading({
        symbol: 'XRP',
        botStatus: true,
        leverage,
        takeProfit: tp,
        stopLoss: sl,
        size: sizeUnits,
        direction: 0, // 롱숏 둘다 허용
        roiTriggers,
        useStopLoss: false
      })

      if (res.statusCode === 200) {
        toast({
          title: t('자동매매 시작'),
          description: res.message || t('자동매매가 시작되었습니다.'),
          status: 'success',
          duration: 2000,
          isClosable: true
        })
        onSeedClose()
        setSeedAmount('')
        await reloadUser()
      }
    } catch (err: any) {
      toast({
        title: t('자동매매 시작 실패'),
        description:
          err.response?.data?.message ||
          err.message ||
          t('알 수 없는 오류가 발생했습니다.'),
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    } finally {
      setIsTradingLoading(false)
    }
  }

  // 관리자용 수동 설정으로 자동매매 시작
  const handleConfirmStartWithManual = async () => {
    setIsTradingLoading(true)
    try {
      const leverage = Number(manualLeverage)
      const tp = Number(manualTakeProfit)
      const sl = Number(manualStopLoss)
      const size = Number(manualSize)

      // 물타기 설정: manualRoiTriggers 배열 사용
      const roiTriggers = manualRoiTriggers.map((item) => ({
        roi: Number(item.roi),
        size: Number(item.size)
      }))

      // XRP 트레이딩 업데이트 및 봇 시작
      const { data: res } = await updateTrading({
        symbol: 'XRP',
        botStatus: true,
        leverage,
        takeProfit: tp,
        stopLoss: sl,
        size,
        direction: 0, // 롱숏 둘다 허용
        roiTriggers,
        useStopLoss: sl > 0
      })

      if (res.statusCode === 200) {
        toast({
          title: t('자동매매 시작'),
          description: res.message || t('자동매매가 시작되었습니다.'),
          status: 'success',
          duration: 2000,
          isClosable: true
        })
        onSeedClose()
        await reloadUser()
      }
    } catch (err: any) {
      toast({
        title: t('자동매매 시작 실패'),
        description:
          err.response?.data?.message ||
          err.message ||
          t('알 수 없는 오류가 발생했습니다.'),
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    } finally {
      setIsTradingLoading(false)
    }
  }

  // 자동매매 종료
  const handleStopAutoTrading = async () => {
    if (!auth?.id || !currentTrading) {
      return
    }

    setIsTradingLoading(true)
    try {
      const { data: res } = await updateTrading({
        symbol: selectedSymbol,
        botStatus: false
      })

      if (res.statusCode === 200) {
        toast({
          title: t('자동매매 종료'),
          description: res.message || t('자동매매가 종료되었습니다.'),
          status: 'success',
          duration: 2000,
          isClosable: true
        })
        await reloadUser()
      }
    } catch (err: any) {
      toast({
        title: t('자동매매 종료 실패'),
        description:
          err.response?.data?.message || t('알 수 없는 오류가 발생했습니다.'),
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    } finally {
      setIsTradingLoading(false)
    }
  }

  // 로그아웃 처리
  const handleLogout = async () => {
    try {
      await signOut()
      await initUser()
      toast({
        title: t('로그아웃'),
        description: t('로그아웃되었습니다.'),
        status: 'success',
        duration: 2000,
        isClosable: true
      })
    } catch (err: any) {
      // 서버 에러가 있어도 로컬 상태는 초기화
      await initUser()
      toast({
        title: t('로그아웃'),
        description: t('로그아웃되었습니다.'),
        status: 'success',
        duration: 2000,
        isClosable: true
      })
    }
  }

  return (
    <DefaultLayout>
      <Box
        minH="100vh"
        bg="dark.bg"
        px={{ base: 4, md: 6 }}
        py={{ base: 8, md: 12 }}
        display="flex"
        alignItems="center"
        justifyContent="center"
        position="relative"
        overflow="hidden"
      >
        {/* 배경 장식 */}
        <Box
          position="absolute"
          top="-20%"
          left="-10%"
          w="600px"
          h="600px"
          bg="brand.500"
          filter="blur(150px)"
          opacity="0.1"
          borderRadius="full"
          zIndex={0}
        />
        <Box
          position="absolute"
          bottom="-10%"
          right="-5%"
          w="500px"
          h="500px"
          bg="purple.500"
          filter="blur(150px)"
          opacity="0.1"
          borderRadius="full"
          zIndex={0}
        />

        <Box maxW={auth?.id ? '2xl' : 'container.xl'} w="full" position="relative" zIndex={1}>
          {/* 로그인 상태에 따라 다른 레이아웃 */}
          {auth?.id ? (
            /* 로그인 후 - 컴팩트한 레이아웃 */
            <Box
              bg="dark.card"
              borderRadius="2xl"
              boxShadow="xl"
              p={{ base: 6, md: 8 }}
              border="1px solid"
              borderColor="whiteAlpha.100"
              backdropFilter="blur(10px)"
            >
              <VStack spacing={5} align="stretch">
                {/* 헤더 - 최소화 */}
                <HStack
                  justify="space-between"
                  align="center"
                  pb={3}
                  borderBottom="1px solid"
                  borderColor="gray.200"
                >
                  <Badge
                    colorScheme={isAutoTradingRunning ? 'green' : 'gray'}
                    fontSize={{ base: 'sm', md: 'md' }}
                    px={4}
                    py={1.5}
                    borderRadius="full"
                    fontWeight="semibold"
                  >
                    {isAutoTradingRunning ? (
                      <HStack spacing={1.5}>
                        <Icon as={CheckCircleIcon} boxSize={4} />
                        <Text>{t('실행중')}</Text>
                      </HStack>
                    ) : (
                      <HStack spacing={1.5}>  
                        <Icon as={CloseIcon} boxSize={3} />
                        <Text>{t('중지됨')}</Text>
                      </HStack>
                    )}
                  </Badge>
              <HStack spacing={2} ml={2} flexWrap="wrap">
                <Badge colorScheme="purple" fontSize="0.8em" p={1} borderRadius="md">
                  SIZE: {currentTrading?.size ?? 0}
                </Badge>
                <Badge colorScheme="blue" fontSize="0.8em" p={1} borderRadius="md">
                  LONG: {currentTrading?.currentAddOrderLong ?? 0} / {currentTrading?.maxAddOrderLong ?? 0}
                </Badge>
                <Badge colorScheme="red" fontSize="0.8em" p={1} borderRadius="md">
                  SHORT: {currentTrading?.currentAddOrderShort ?? 0} / {currentTrading?.maxAddOrderShort ?? 0}
                </Badge>
              </HStack>
                </HStack>

                {/* 로그인 사용자 정보 */}
                <Box
                  bg="whiteAlpha.50"
                  borderRadius="lg"
                  p={3}
                  borderWidth="1px"
                  borderColor="whiteAlpha.100"
                >
                  <VStack spacing={1} align="stretch">
                    <Text fontSize="sm" color="gray.400" fontWeight="semibold">
                      로그인 정보
                    </Text>
                    <HStack spacing={2}>
                      <Text fontSize="sm" color="gray.500">
                        아이디:
                      </Text>
                      <Text fontSize="sm" color="white" fontWeight="bold">
                        {auth?.account || '-'}
                      </Text>
                    </HStack>
                    <HStack spacing={2}>
                      <Text fontSize="sm" color="gray.500">
                        이름:
                      </Text>
                      <Text fontSize="sm" color="white" fontWeight="bold">
                        {auth?.username || '-'}
                      </Text>
                    </HStack>
                  </VStack>
                </Box>

                {/* AI 작동 오버레이 (비주얼) - 실행 중일 때만 표시 */}
                {isAutoTradingRunning && <AiWorkingOverlay />}

                {/* 자동매매 시작/종료 버튼 - 메인 */}
                <Button
                  h={{ base: '60px', md: '70px' }}
                  w="full"
                  fontSize={{ base: 'xl', md: '2xl' }}
                  fontWeight="bold"
                  colorScheme={isAutoTradingRunning ? 'red' : 'blue'}
                  onClick={
                    isAutoTradingRunning
                      ? handleStopAutoTrading
                      : handleStartAutoTrading
                  }
                  isLoading={isTradingLoading}
                  loadingText={
                    isAutoTradingRunning ? t('종료 중...') : t('시작 중...')
                  }
                  disabled={!currentTrading}
                  borderRadius="xl"
                  boxShadow="lg"
                  _hover={{
                    transform: 'scale(1.02)',
                    boxShadow: 'xl'
                  }}
                  transition="all 0.2s"
                  leftIcon={
                    isAutoTradingRunning ? (
                      <Icon as={CloseIcon} boxSize={6} />
                    ) : (
                      <Icon as={CheckCircleIcon} boxSize={6} />
                    )
                  }
                >
                  {isAutoTradingRunning
                    ? t('자동매매 종료')
                    : t('자동매매 시작')}
                </Button>

                {/* 하단 메뉴 버튼들 */}
                <VStack spacing={3} align="stretch" pt={2}>
                  <Button
                    h={{ base: '52px', md: '60px' }}
                    w="full"
                    fontSize={{ base: 'md', md: 'lg' }}
                    fontWeight="semibold"
                    variant="outline"
                    colorScheme="purple"
                    onClick={onApiOpen}
                    borderRadius="xl"
                    borderColor="whiteAlpha.200"
                    color="white"
                    leftIcon={<Icon as={SettingsIcon} boxSize={5} />}
                    _hover={{
                      bg: 'whiteAlpha.100',
                      transform: 'scale(1.01)'
                    }}
                    transition="all 0.2s"
                  >
                    {t('API 관리')}
                  </Button>

                  {/* 관리자 페이지 버튼 - 관리자만 표시 */}
                  {auth?.isAdmin === 1 && (
                    <Button
                      h={{ base: '52px', md: '60px' }}
                      w="full"
                      fontSize={{ base: 'md', md: 'lg' }}
                      fontWeight="semibold"
                      variant="outline"
                      colorScheme="blue"
                      onClick={() => navigate('/admin/user')}
                      borderRadius="xl"
                      borderColor="whiteAlpha.200"
                      color="white"
                      leftIcon={<Icon as={FiSettings} boxSize={5} />}
                      _hover={{
                        bg: 'whiteAlpha.100',
                        transform: 'scale(1.01)'
                      }}
                      transition="all 0.2s"
                    >
                      {t('관리자 페이지')}
                    </Button>
                  )}

                  <Button
                    h={{ base: '44px', md: '52px' }}
                    w="full"
                    fontSize={{ base: 'sm', md: 'md' }}
                    variant="ghost"
                    colorScheme="gray"
                    color="gray.500"
                    onClick={handleLogout}
                    borderRadius="xl"
                    _hover={{
                      bg: 'whiteAlpha.100',
                      color: 'white'
                    }}
                  >
                    {t('로그아웃')}
                  </Button>
                </VStack>
              </VStack>
            </Box>
          ) : (
            /* 로그인 전 - 소개 레이아웃 (Realtime List) */
            <VStack spacing={8} align="stretch">
              <Box textAlign="center" mb={4}>
                <Heading
                  as="h1"
                  size="2xl"
                  bgGradient="linear(to-r, brand.400, brand.600)"
                  bgClip="text"
                  mb={4}
                  className="tracking-tight"
                >
                  WaveBot AI Auto Trading
                </Heading>
                <Text fontSize="xl" color="gray.400" maxW="2xl" mx="auto">
                  {t(
                    '최첨단 AI 알고리즘이 24시간 시장을 분석하여 최적의 타이밍에 자동으로 매매를 수행합니다.'
                  )}
                </Text>
              </Box>

              {/* Realtime List (Logged Out View) */}
              <Box>
                <Text fontSize="xl" fontWeight="bold" color="white" mb={4}>
                  {t('실시간 포지션 현황')}
                </Text>
                <RealtimeList />
              </Box>

              <Flex justify="center" mt={8}>
                <Button
                  size="lg"
                  colorScheme="brand"
                  px={12}
                  h={16}
                  fontSize="xl"
                  onClick={onLoginOpen}
                  _hover={{
                    transform: 'translateY(-2px)',
                    boxShadow: 'xl'
                  }}
                  className="group"
                >
                  {t('로그인하고 자동매매 시작하기')}
                </Button>
              </Flex>

              {/* Telegram Inquiry */}
              <Box
                mt={12}
                p={8}
                bg="whiteAlpha.50"
                borderRadius="2xl"
                border="1px solid"
                borderColor="whiteAlpha.100"
                textAlign="center"
              >
                <VStack spacing={4}>
                  <Heading size="md" color="white">
                    {t('궁금한 점이 있으신가요?')}
                  </Heading>
                  <Text color="gray.400">
                    {t(
                      '텔레그램 채널에서 더 많은 정보와 실시간 지원을 받아보세요.'
                    )}
                  </Text>
                  <Button
                    as="a"
                    href="https://t.me/wave_labs"
                    target="_blank"
                    colorScheme="telegram"
                    leftIcon={<FaTelegram />}
                    size="lg"
                  >
                    {t('텔레그램 문의하기')}
                  </Button>
                </VStack>
              </Box>
            </VStack>
          )}
        </Box>

        {/* 로그인 모달 */}
        <Modal isOpen={isLoginOpen} onClose={onLoginClose} isCentered size="lg">
          <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(5px)" />
          <ModalContent borderRadius="2xl" mx={4} bg="dark.card" border="1px solid" borderColor="whiteAlpha.200">
            <ModalHeader
              fontSize={{ base: '2xl', md: '3xl' }}
              fontWeight="bold"
              textAlign="center"
              pb={4}
              color="white"
            >
              {t('로그인')}
            </ModalHeader>
            <ModalCloseButton size="lg" color="white" />
            <ModalBody pb={6}>
              <VStack spacing={6}>
                <FormControl>
                  <FormLabel
                    fontSize={{ base: 'lg', md: 'xl' }}
                    fontWeight="semibold"
                    mb={3}
                    color="gray.300"
                  >
                    {t('아이디')}
                  </FormLabel>
                  <Input
                    placeholder={t('아이디를 입력하세요')}
                    value={account}
                    onChange={(e) => setAccount(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleLogin()
                      }
                    }}
                    h={{ base: '56px', md: '64px' }}
                    fontSize={{ base: 'lg', md: 'xl' }}
                    borderRadius="xl"
                    borderWidth="1px"
                    borderColor="whiteAlpha.200"
                    bg="whiteAlpha.100"
                    color="white"
                    _placeholder={{ color: 'gray.500' }}
                    _focus={{
                      borderColor: 'brand.500',
                      boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)'
                    }}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel
                    fontSize={{ base: 'lg', md: 'xl' }}
                    fontWeight="semibold"
                    mb={3}
                    color="gray.300"
                  >
                    {t('비밀번호')}
                  </FormLabel>
                  <Input
                    placeholder={t('비밀번호를 입력하세요')}
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleLogin()
                      }
                    }}
                    h={{ base: '56px', md: '64px' }}
                    fontSize={{ base: 'lg', md: 'xl' }}
                    borderRadius="xl"
                    borderWidth="1px"
                    borderColor="whiteAlpha.200"
                    bg="whiteAlpha.100"
                    color="white"
                    _placeholder={{ color: 'gray.500' }}
                    _focus={{
                      borderColor: 'brand.500',
                      boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)'
                    }}
                  />
                </FormControl>
              </VStack>
            </ModalBody>
            <ModalFooter pt={4} pb={6}>
              <VStack w="full" spacing={3}>
                <Button
                  h={{ base: '56px', md: '64px' }}
                  w="full"
                  fontSize={{ base: 'xl', md: '2xl' }}
                  fontWeight="bold"
                  bgGradient="linear(to-r, brand.500, brand.600)"
                  color="white"
                  _hover={{
                    bgGradient: 'linear(to-r, brand.600, brand.700)',
                    transform: 'translateY(-2px)',
                    boxShadow: 'lg'
                  }}
                  onClick={handleLogin}
                  isLoading={isLoggingIn}
                  loadingText={t('로그인 중...')}
                  borderRadius="xl"
                  leftIcon={<Icon as={LockIcon} boxSize={6} />}
                >
                  {t('로그인')}
                </Button>
                <Button
                  h={{ base: '48px', md: '56px' }}
                  w="full"
                  fontSize={{ base: 'md', md: 'lg' }}
                  variant="ghost"
                  onClick={onLoginClose}
                  isDisabled={isLoggingIn}
                  borderRadius="xl"
                  color="gray.400"
                  _hover={{ bg: 'whiteAlpha.100', color: 'white' }}
                >
                  {t('취소')}
                </Button>
              </VStack>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* 시드 입력 모달 (관리자는 스위치 옵션 포함) */}
        <Modal
          isOpen={isSeedOpen}
          onClose={onSeedClose}
          isCentered
          size={useManualConfig && isAdmin ? "2xl" : "md"}
        >
          <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(5px)" />
          <ModalContent borderRadius="2xl" mx={4} maxH="90vh" overflow="hidden" bg="dark.card" border="1px solid" borderColor="whiteAlpha.200">
            <ModalHeader
              fontSize={{ base: 'xl', md: '2xl' }}
              fontWeight="bold"
              textAlign="center"
              pb={2}
              color="white"
            >
              {t('자동매매 시작')}
            </ModalHeader>
            <ModalCloseButton size="lg" color="white" />
            <ModalBody pb={4} overflowY="auto">
              <VStack spacing={4} align="stretch">
                {/* 관리자용 스위치 */}
                {isAdmin && (
                  <Box
                    p={3}
                    bg="whiteAlpha.50"
                    borderRadius="xl"
                    borderWidth="1px"
                    borderColor="whiteAlpha.200"
                  >
                    <HStack justify="space-between">
                      <VStack align="start" spacing={0}>
                        <Text fontWeight="bold" color="brand.200">
                          {t('수동 설정 모드')}
                        </Text>
                        <Text fontSize="xs" color="gray.400">
                          {t('레버리지, 물타기 등을 직접 설정합니다')}
                        </Text>
                      </VStack>
                      <Switch
                        size="lg"
                        colorScheme="brand"
                        isChecked={useManualConfig}
                        onChange={(e) => setUseManualConfig(e.target.checked)}
                      />
                    </HStack>
                  </Box>
                )}

                {/* 수동 설정 모드 */}
                {useManualConfig && isAdmin ? (
                  <VStack spacing={4} align="stretch">
                    <FormControl>
                      <FormLabel fontSize="md" fontWeight="semibold" color="gray.300">
                        {t('레버리지')}
                      </FormLabel>
                      <Input
                        type="number"
                        value={manualLeverage}
                        onChange={(e) => setManualLeverage(e.target.value)}
                        h="52px"
                        fontSize="md"
                        borderRadius="xl"
                        borderWidth="1px"
                        borderColor="whiteAlpha.200"
                        bg="whiteAlpha.100"
                        color="white"
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel fontSize="md" fontWeight="semibold" color="gray.300">
                        {t('익절 (%)')}
                      </FormLabel>
                      <Input
                        type="number"
                        value={manualTakeProfit}
                        onChange={(e) => setManualTakeProfit(e.target.value)}
                        h="52px"
                        fontSize="md"
                        borderRadius="xl"
                        borderWidth="1px"
                        borderColor="whiteAlpha.200"
                        bg="whiteAlpha.100"
                        color="white"
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel fontSize="md" fontWeight="semibold" color="gray.300">
                        {t('손절 (%)')}
                      </FormLabel>
                      <Input
                        type="number"
                        value={manualStopLoss}
                        onChange={(e) => setManualStopLoss(e.target.value)}
                        h="52px"
                        fontSize="md"
                        borderRadius="xl"
                        borderWidth="1px"
                        borderColor="whiteAlpha.200"
                        bg="whiteAlpha.100"
                        color="white"
                      />
                    </FormControl>

                    <FormControl>
                      <HStack justify="space-between" mb={2}>
                        <FormLabel fontSize="md" fontWeight="semibold" mb={0} color="gray.300">
                          {t('사이즈 (Size)')}
                        </FormLabel>
                        <Button
                          size="xs"
                          variant={showCalculator ? 'solid' : 'outline'}
                          colorScheme="brand"
                          onClick={() => {
                            if (!showCalculator) {
                              setCalcDivide(manualDcaCount)
                            }
                            setShowCalculator(!showCalculator)
                          }}
                        >
                          {showCalculator ? t('계산기 닫기') : t('계산기 열기')}
                        </Button>
                      </HStack>
                      <Input
                        type="number"
                        value={manualSize}
                        onChange={(e) => setManualSize(e.target.value)}
                        h="52px"
                        fontSize="md"
                        borderRadius="xl"
                        borderWidth="1px"
                        borderColor="whiteAlpha.200"
                        bg="whiteAlpha.100"
                        color="white"
                      />
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        1 size = 10 XRP
                      </Text>

                      {/* 사이즈 계산기 */}
                      {showCalculator && (
                        <VStack
                          mt={3}
                          p={4}
                          bg="whiteAlpha.50"
                          borderRadius="xl"
                          borderWidth="1px"
                          borderColor="whiteAlpha.200"
                          spacing={3}
                          align="stretch"
                        >
                          <Text fontSize="sm" fontWeight="bold" color="brand.200">
                            📊 {t('사이즈 계산기')}
                          </Text>

                          <FormControl>
                            <FormLabel fontSize="xs" mb={1} color="gray.400">
                              {t('XRP 현재 가격')}
                            </FormLabel>
                            <Input
                              type="number"
                              value={xrpPrice}
                              isReadOnly
                              bg="whiteAlpha.100"
                              color="white"
                              border="none"
                              size="sm"
                              fontSize="sm"
                            />
                          </FormControl>

                          <FormControl>
                            <FormLabel fontSize="xs" mb={1} color="gray.400">
                              {t('전체 시드 (USDT)')}
                            </FormLabel>
                            <Input
                              type="number"
                              value={calcSeed}
                              onChange={(e) => setCalcSeed(e.target.value)}
                              size="sm"
                              fontSize="sm"
                              placeholder="1000"
                              bg="whiteAlpha.100"
                              color="white"
                              border="none"
                            />
                          </FormControl>

                          <FormControl>
                            <FormLabel fontSize="xs" mb={1} color="gray.400">
                              {t('시드 사용 비율: {{percent}}%', { percent: calcSeedPercent })}
                            </FormLabel>
                            <HStack spacing={2}>
                              <Slider
                                value={calcSeedPercent}
                                onChange={(val) => setCalcSeedPercent(val)}
                                min={1}
                                max={100}
                                step={1}
                                flex="1"
                                colorScheme="brand"
                              >
                                <SliderTrack bg="whiteAlpha.200">
                                  <SliderFilledTrack />
                                </SliderTrack>
                                <SliderThumb />
                              </Slider>
                              <Input
                                type="number"
                                value={calcSeedPercent}
                                onChange={(e) => setCalcSeedPercent(Number(e.target.value))}
                                min={1}
                                max={100}
                                w="60px"
                                size="sm"
                                fontSize="sm"
                                bg="whiteAlpha.100"
                                color="white"
                                border="none"
                              />
                            </HStack>
                            <HStack mt={1} spacing={1} flexWrap="wrap">
                              {[10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map((percent) => (
                                <Button
                                  key={percent}
                                  size="xs"
                                  variant={calcSeedPercent === percent ? 'solid' : 'outline'}
                                  colorScheme={calcSeedPercent === percent ? 'brand' : 'gray'}
                                  onClick={() => setCalcSeedPercent(percent)}
                                  fontSize="xs"
                                  h="20px"
                                  minW="32px"
                                >
                                  {percent}%
                                </Button>
                              ))}
                            </HStack>
                            <Text fontSize="xs" color="gray.500" mt={1}>
                              {t('사용할 시드:')} ${((Number(calcSeed) || 0) * (calcSeedPercent / 100)).toFixed(2)}
                            </Text>
                          </FormControl>

                          <FormControl>
                            <FormLabel fontSize="xs" mb={1} color="gray.400">
                              {t('나눔 횟수 (물타기 회수)')}
                            </FormLabel>
                            <Input
                              type="number"
                              value={calcDivide}
                              onChange={(e) => setCalcDivide(e.target.value)}
                              size="sm"
                              fontSize="sm"
                              placeholder="50"
                              bg="whiteAlpha.100"
                              color="white"
                              border="none"
                            />
                          </FormControl>

                          <Box
                            p={3}
                            bg="whiteAlpha.50"
                            borderRadius="lg"
                            borderWidth="1px"
                            borderColor="whiteAlpha.200"
                          >
                            <VStack spacing={1} align="stretch">
                              <HStack justify="space-between" fontSize="xs">
                                <Text color="gray.400">{t('1회 진입 금액:')}</Text>
                                <Text fontWeight="semibold" color="white">
                                  ${(((Number(calcSeed) || 0) * (calcSeedPercent / 100)) / (Number(calcDivide) || 1)).toFixed(2)}
                                </Text>
                              </HStack>
                              <HStack justify="space-between" fontSize="xs">
                                <Text color="gray.400">{t('레버리지 적용:')}</Text>
                                <Text fontWeight="semibold" color="white">
                                  ${((((Number(calcSeed) || 0) * (calcSeedPercent / 100)) / (Number(calcDivide) || 1)) * (Number(manualLeverage) || 1)).toFixed(2)}
                                </Text>
                              </HStack>
                              <HStack justify="space-between" fontSize="xs">
                                <Text color="gray.400">{t('코인 수량:')}</Text>
                                <Text fontWeight="semibold" color="white">
                                  {Math.floor(((((Number(calcSeed) || 0) * (calcSeedPercent / 100)) / (Number(calcDivide) || 1)) * (Number(manualLeverage) || 1)) / (Number(xrpPrice) || 1))} XRP
                                </Text>
                              </HStack>
                              <Divider my={1} borderColor="whiteAlpha.200" />
                              <HStack justify="space-between">
                                <Text fontSize="sm" fontWeight="bold" color="brand.200">
                                  {t('계산된 사이즈:')}
                                </Text>
                                <Text fontSize="lg" fontWeight="bold" color="brand.200">
                                  {calculatedSize} size
                                </Text>
                              </HStack>
                            </VStack>
                          </Box>

                          <Button
                            size="sm"
                            colorScheme="brand"
                            onClick={handleApplyCalculatedSize}
                            isDisabled={calculatedSize === 0}
                          >
                            {t('계산된 사이즈 적용')}
                          </Button>
                        </VStack>
                      )}
                    </FormControl>

                    <FormControl>
                      <FormLabel fontSize="md" fontWeight="semibold" color="gray.300">
                        {t('물타기 회수')}
                      </FormLabel>
                      <Input
                        type="number"
                        value={manualDcaCount}
                        onChange={(e) => setManualDcaCount(e.target.value)}
                        h="52px"
                        fontSize="md"
                        borderRadius="xl"
                        borderWidth="1px"
                        borderColor="whiteAlpha.200"
                        bg="whiteAlpha.100"
                        color="white"
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel fontSize="md" fontWeight="semibold" color="gray.300">
                        {t('ROI 트리거 (%)')}
                      </FormLabel>
                      <Input
                        type="number"
                        value={manualRoi}
                        onChange={(e) => setManualRoi(e.target.value)}
                        h="52px"
                        fontSize="md"
                        borderRadius="xl"
                        borderWidth="1px"
                        borderColor="whiteAlpha.200"
                        bg="whiteAlpha.100"
                        color="white"
                        placeholder="-30"
                      />
                    </FormControl>

                    <Divider my={4} borderColor="whiteAlpha.200" />

                    {/* 고급 물타기 설정 */}
                    <Box>
                      <HStack justify="space-between" mb={3}>
                        <Text fontSize="md" fontWeight="bold" color="white">
                          {t('물타기 전략 (고급 설정)')}
                        </Text>
                        <HStack spacing={2}>
                          <Button
                            size="xs"
                            variant="outline"
                            colorScheme="red"
                            onClick={() => setManualRoiTriggers([])}
                          >
                            {t('초기화')}
                          </Button>
                          <Button 
                            size="xs" 
                            variant="outline"
                            colorScheme="brand"
                            onClick={handleBatchSetupManual}
                          >
                            {t('일괄 생성')}
                          </Button>
                        </HStack>
                      </HStack>

                      <VStack align="stretch" spacing={3}>
                        {/* 헤더 */}
                        <Box
                          display="grid"
                          gridTemplateColumns="60px 1fr 1fr 32px"
                          px={1}
                          fontWeight="semibold"
                          color="gray.400"
                          fontSize="xs"
                        >
                          <Text textAlign="center">{t('회차')}</Text>
                          <Text textAlign="center">ROI (%)</Text>
                          <Text textAlign="center">{t('사이즈')}</Text>
                          <Box />
                        </Box>

                        {/* 물타기 단계 목록 */}
                        <Box maxH="250px" overflowY="auto" pr={1}>
                          {manualRoiTriggers.length === 0 ? (
                            <Box textAlign="center" color="gray.500" py={3} fontSize="sm">
                              {t('물타기 미사용 상태입니다.')}
                              <br />
                              <Text fontSize="xs" color="gray.600" mt={1}>
                                {t('일괄 생성 또는 추가 버튼을 눌러주세요')}
                              </Text>
                            </Box>
                          ) : (
                            manualRoiTriggers.map((item, index) => (
                              <Box
                                key={index}
                                display="grid"
                                gridTemplateColumns="60px 1fr 1fr 32px"
                                gap={2}
                                alignItems="center"
                                mb={2}
                              >
                                {/* 회차 */}
                                <Text textAlign="center" fontSize="xs" color="gray.400">
                                  {index + 1}
                                </Text>

                                {/* ROI 입력 */}
                                <Input
                                  type="number"
                                  size="sm"
                                  value={item.roi}
                                  onChange={(e) => {
                                    const updated = [...manualRoiTriggers]
                                    updated[index].roi = e.target.value
                                    setManualRoiTriggers(updated)
                                  }}
                                  onBlur={() => {
                                    if (!item.roi || Number(item.roi) === 0) {
                                      const updated = [...manualRoiTriggers]
                                      updated[index].roi = '-30'
                                      setManualRoiTriggers(updated)
                                    }
                                  }}
                                  placeholder="ROI"
                                  fontSize="xs"
                                  bg="whiteAlpha.100"
                                  color="white"
                                  border="none"
                                />

                                {/* 사이즈 입력 */}
                                <Input
                                  type="number"
                                  size="sm"
                                  value={item.size}
                                  onChange={(e) => {
                                    const updated = [...manualRoiTriggers]
                                    updated[index].size = e.target.value
                                    setManualRoiTriggers(updated)
                                  }}
                                  onBlur={() => {
                                    if (!item.size || Number(item.size) < 1) {
                                      const updated = [...manualRoiTriggers]
                                      updated[index].size = '1'
                                      setManualRoiTriggers(updated)
                                    }
                                  }}
                                  placeholder={t('사이즈') as string}
                                  fontSize="xs"
                                  bg="whiteAlpha.100"
                                  color="white"
                                  border="none"
                                />

                                {/* 삭제 버튼 */}
                                <IconButton
                                  aria-label="delete"
                                  icon={<CloseIcon />}
                                  size="xs"
                                  onClick={() => removeManualRoiTrigger(index)}
                                  variant="ghost"
                                  colorScheme="red"
                                />
                              </Box>
                            ))
                          )}
                        </Box>

                        <Button
                          leftIcon={<AddIcon />}
                          w="100%"
                          size="sm"
                          onClick={addManualRoiTrigger}
                          colorScheme="brand"
                          variant="outline"
                        >
                          {t('물타기 단계 추가')}
                        </Button>
                      </VStack>
                    </Box>

                    <Divider my={4} borderColor="whiteAlpha.200" />

                    <Box
                      bg="whiteAlpha.50"
                      p={3}
                      borderRadius="xl"
                      borderWidth="1px"
                      borderColor="whiteAlpha.200"
                    >
                      <Text fontSize="xs" color="gray.400">
                        <Text as="span" fontWeight="bold" color="white">설정 요약:</Text>
                        <br />
                        • 레버리지: {manualLeverage}배
                        <br />
                        • 익절: {manualTakeProfit}%
                        <br />
                        • 손절: {manualStopLoss}%
                        <br />
                        • 사이즈: {manualSize} (= {Number(manualSize) * 10} XRP)
                        <br />
                        • 물타기 단계: {manualRoiTriggers.length}개
                      </Text>
                    </Box>
                  </VStack>
                ) : (
                  /* 시드 입력 모드 */
                  <>
                    <FormControl mb={6}>
                      <FormLabel fontSize="lg" fontWeight="semibold" mb={3} color="gray.300">
                        {t('전략 선택 (보유:진입 비율)')}
                      </FormLabel>
                      <RadioGroup onChange={setStrategyRatio} value={strategyRatio}>
                        <Stack direction="column" spacing={3}>
                          <Box 
                            p={3} 
                            border="1px solid" 
                            borderColor={strategyRatio === '9:1' ? 'brand.500' : 'whiteAlpha.200'}
                            borderRadius="lg"
                            bg={strategyRatio === '9:1' ? 'whiteAlpha.100' : 'transparent'}
                            cursor="pointer"
                            onClick={() => setStrategyRatio('9:1')}
                          >
                            <Radio value="9:1" colorScheme="brand">
                              <HStack spacing={2}>
                                <Text fontWeight="bold" color="white">안전형</Text>
                                <Badge colorScheme="green">Risk Low</Badge>
                              </HStack>
                            </Radio>
                          </Box>

                          <Box 
                            p={3} 
                            border="1px solid" 
                            borderColor={strategyRatio === '8:2' ? 'brand.500' : 'whiteAlpha.200'}
                            borderRadius="lg"
                            bg={strategyRatio === '8:2' ? 'whiteAlpha.100' : 'transparent'}
                            cursor="pointer"
                            onClick={() => setStrategyRatio('8:2')}
                          >
                            <Radio value="8:2" colorScheme="brand">
                              <HStack spacing={2}>
                                <Text fontWeight="bold" color="white">표준형</Text>
                                <Badge colorScheme="blue">Risk Medium</Badge>
                              </HStack>
                            </Radio>
                          </Box>

                          <Box 
                            p={3} 
                            border="1px solid" 
                            borderColor={strategyRatio === '7:3' ? 'brand.500' : 'whiteAlpha.200'}
                            borderRadius="lg"
                            bg={strategyRatio === '7:3' ? 'whiteAlpha.100' : 'transparent'}
                            cursor="pointer"
                            onClick={() => setStrategyRatio('7:3')}
                          >
                            <Radio value="7:3" colorScheme="brand">
                              <HStack spacing={2}>
                                <Text fontWeight="bold" color="white">공격형</Text>
                                <Badge colorScheme="red">Risk High</Badge>
                              </HStack>
                            </Radio>

                          </Box>
                        </Stack>
                      </RadioGroup>
                    </FormControl>


                    <FormControl>
                      <FormLabel
                        fontSize={{ base: 'md', md: 'lg' }}
                        fontWeight="semibold"
                        mb={2}
                        color="gray.300"
                      >
                        {t('시드 금액 (USDT)')}
                      </FormLabel>
                      <Input
                        type="number"
                        placeholder="1000"
                        value={seedAmount}
                        onChange={(e) => setSeedAmount(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleConfirmStartWithSeed()
                          }
                        }}
                        h={{ base: '52px', md: '56px' }}
                        fontSize={{ base: 'md', md: 'lg' }}
                        borderRadius="xl"
                        borderWidth="1px"
                        borderColor="whiteAlpha.200"
                        bg="whiteAlpha.100"
                        color="white"
                        _placeholder={{ color: 'gray.500' }}
                        _focus={{
                          borderColor: 'brand.500',
                          boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)'
                        }}
                      />
                    </FormControl>

                    {seedAmount &&
                      !isNaN(Number(seedAmount)) &&
                      Number(seedAmount) > 0 && (
                        <Box
                          bg="whiteAlpha.50"
                          p={3}
                          borderRadius="lg"
                          w="full"
                          borderWidth="1px"
                          borderColor="whiteAlpha.100"
                        >
                          <HStack justify="space-between" fontSize="sm">
                            <Text fontWeight="semibold" color="gray.400">
                              {t('사용 시드:')}
                            </Text>
                            <Text fontWeight="bold" color="green.400">
                              ${(Number(seedAmount) * (strategyRatio === '9:1' ? 0.1 : strategyRatio === '8:2' ? 0.2 : 0.3)).toFixed(2)}
                            </Text>
                          </HStack>
                          <HStack justify="space-between" fontSize="sm" mt={1}>
                            <Text fontWeight="semibold" color="gray.400">
                              {t('1회 진입시 마진:')}
                            </Text>
                            <Text fontWeight="bold" color="green.400">
                              ${((Number(seedAmount) * (strategyRatio === '9:1' ? 0.1 : strategyRatio === '8:2' ? 0.2 : 0.3)) / 100).toFixed(2)}
                            </Text>
                          </HStack>
                        </Box>
                      )}
                  </>
                )}
              </VStack>
            </ModalBody>
            <ModalFooter pt={2} pb={4}>
              <VStack w="full" spacing={3}>
                <Button
                  h={{ base: '56px', md: '64px' }}
                  w="full"
                  fontSize={{ base: 'xl', md: '2xl' }}
                  fontWeight="bold"
                  bgGradient="linear(to-r, brand.500, brand.600)"
                  color="white"
                  _hover={{
                    bgGradient: 'linear(to-r, brand.600, brand.700)',
                    transform: 'translateY(-2px)',
                    boxShadow: 'lg'
                  }}
                  onClick={useManualConfig && isAdmin ? handleConfirmStartWithManual : handleConfirmStartWithSeed}
                  isLoading={isTradingLoading}
                  loadingText={t('시작 중...')}
                  borderRadius="xl"
                  leftIcon={<Icon as={CheckCircleIcon} boxSize={6} />}
                >
                  {t('자동매매 시작')}
                </Button>
                <Button
                  h={{ base: '48px', md: '56px' }}
                  w="full"
                  fontSize={{ base: 'md', md: 'lg' }}
                  variant="ghost"
                  onClick={onSeedClose}
                  isDisabled={isTradingLoading}
                  borderRadius="xl"
                  color="gray.400"
                  _hover={{ bg: 'whiteAlpha.100', color: 'white' }}
                >
                  {t('취소')}
                </Button>
              </VStack>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* API 관리 모달 */}
        <Modal isOpen={isApiOpen} onClose={onApiClose} isCentered size="lg">
          <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(5px)" />
          <ModalContent borderRadius="2xl" mx={4} bg="dark.card" border="1px solid" borderColor="whiteAlpha.200">
            <ModalHeader
              fontSize={{ base: '2xl', md: '3xl' }}
              fontWeight="bold"
              textAlign="center"
              pb={4}
              color="white"
            >
              {t('API 키 관리')}
            </ModalHeader>
            <ModalCloseButton size="lg" color="white" />
            <ModalBody pb={6}>
              <VStack spacing={6} align="stretch">
                <Box mb={4}>
                  <ApiManual />
                </Box>

                <Divider borderColor="whiteAlpha.200" />

                <FormControl>
                  <FormLabel
                    fontSize={{ base: 'lg', md: 'xl' }}
                    fontWeight="semibold"
                    mb={3}
                    color="gray.300"
                  >
                    {t('API Key')}
                  </FormLabel>
                  <Input
                    placeholder={t('거래소에서 발급받은 API Key를 입력하세요')}
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    h={{ base: '56px', md: '64px' }}
                    fontSize={{ base: 'lg', md: 'xl' }}
                    borderRadius="xl"
                    borderWidth="1px"
                    borderColor="whiteAlpha.200"
                    bg="whiteAlpha.100"
                    color="white"
                    _placeholder={{ color: 'gray.500' }}
                    _focus={{
                      borderColor: 'brand.500',
                      boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)'
                    }}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel
                    fontSize={{ base: 'lg', md: 'xl' }}
                    fontWeight="semibold"
                    mb={3}
                    color="gray.300"
                  >
                    {t('API Secret')}
                  </FormLabel>
                  <Input
                    placeholder={t('거래소에서 발급받은 API Secret을 입력하세요')}
                    type={auth.apiSecret ? 'password' : 'text'}
                    value={apiSecret}
                    onChange={(e) => setApiSecret(e.target.value)}
                    h={{ base: '56px', md: '64px' }}
                    fontSize={{ base: 'lg', md: 'xl' }}
                    borderRadius="xl"
                    borderWidth="1px"
                    borderColor="whiteAlpha.200"
                    bg="whiteAlpha.100"
                    color="white"
                    _placeholder={{ color: 'gray.500' }}
                    _focus={{
                      borderColor: 'brand.500',
                      boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)'
                    }}
                  />
                </FormControl>
              </VStack>
            </ModalBody>
            <ModalFooter pt={4} pb={6}>
              <VStack w="full" spacing={3}>
                <Button
                  h={{ base: '56px', md: '64px' }}
                  w="full"
                  fontSize={{ base: 'xl', md: '2xl' }}
                  fontWeight="bold"
                  bgGradient="linear(to-r, brand.500, brand.600)"
                  color="white"
                  _hover={{
                    bgGradient: 'linear(to-r, brand.600, brand.700)',
                    transform: 'translateY(-2px)',
                    boxShadow: 'lg'
                  }}
                  onClick={handleSaveApi}
                  borderRadius="xl"
                  leftIcon={<Icon as={SettingsIcon} boxSize={6} />}
                >
                  {t('저장')}
                </Button>
                <Button
                  h={{ base: '48px', md: '56px' }}
                  w="full"
                  fontSize={{ base: 'md', md: 'lg' }}
                  variant="ghost"
                  onClick={onApiClose}
                  borderRadius="xl"
                  color="gray.400"
                  _hover={{ bg: 'whiteAlpha.100', color: 'white' }}
                >
                  {t('취소')}
                </Button>
              </VStack>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Box>
    </DefaultLayout>
  )
}

export default Home
